import * as THREE from 'three';
import { springStepAngle, springStepScalar, springStepVec3 } from '../util/Spring';
import { locateTrackPosition, sampleCenterlineAhead, type Track } from '../track/TrackLoader';

export type CameraMode = 'topDown' | 'chase';

// Slow handheld feel: 0.18 rad/s ≈ a full revolution every 35s. Faster would
// distract from the title UI; slower would feel static at first glance.
const CINEMATIC_ORBIT_RADS_PER_SECOND = 0.18;
const CINEMATIC_ORBIT_RADIUS = 14;
const CINEMATIC_ORBIT_HEIGHT = 3.2;
const CINEMATIC_LOOK_AT_HEIGHT = 0.7;

export type GroundHeightSampler = (x: number, z: number) => number;

export interface FollowCameraOptions {
  mode?: CameraMode;
  height?: number;
  tiltDeg?: number;
  topLookAhead?: number;
  topLeadResponsiveness?: number;
  topLeadFullSpeed?: number;
  topFollowResponsiveness?: number;
  topFovDeg?: number;
  chaseDistance?: number;
  chaseHeight?: number;
  chaseLookAhead?: number;
  chasePositionResponsiveness?: number;
  chaseLookResponsiveness?: number;
  chaseTargetHeight?: number;
  chaseVelocityCompensation?: number;
  chaseFovDeg?: number;
  chaseFovBoostDeg?: number;
  chaseFovFullSpeed?: number;
  chaseFovResponsiveness?: number;
  chaseHeadingVelocityBlend?: number;
  chaseHeadingDeadzoneYawRate?: number;
  chaseHeadingDeadzoneLateralSpeed?: number;
  chaseMaxYawLagDeg?: number;
  chaseTurnHeightBoost?: number;
  chaseTurnHeightFullLateral?: number;
  apexAimEnabled?: boolean;
  apexLookAhead?: number;
  apexLookAheadSpeedGain?: number;
  apexMaxLateralOffset?: number;
  apexOffTrackFadeStart?: number;
  apexOffTrackFadeEnd?: number;
  lookSteerBias?: number;
  cameraLateralLean?: number;
  bankMaxRollDeg?: number;
  bankHalflife?: number;
  highSpeedShakeAmp?: number;
  accelLagMaxDistance?: number;
  accelLagResponsiveness?: number;
  accelLagReturnResponsiveness?: number;
  modeTransitionDuration?: number;
}

/**
 * Tunable racer camera.
 * topDown keeps the world axis-aligned; chase follows behind the racer heading.
 */
export class FollowCamera {
  readonly camera: THREE.PerspectiveCamera;
  mode: CameraMode;
  height: number;
  tiltDeg: number;
  topLookAhead: number;
  topLeadResponsiveness: number;
  topLeadFullSpeed: number;
  topFollowResponsiveness: number;
  topFovDeg: number;
  chaseDistance: number;
  chaseHeight: number;
  chaseLookAhead: number;
  chasePositionResponsiveness: number;
  chaseLookResponsiveness: number;
  chaseTargetHeight: number;
  chaseVelocityCompensation: number;
  chaseFovDeg: number;
  chaseFovBoostDeg: number;
  chaseFovFullSpeed: number;
  chaseFovResponsiveness: number;
  chaseHeadingVelocityBlend: number;
  chaseHeadingDeadzoneYawRate: number;
  chaseHeadingDeadzoneLateralSpeed: number;
  chaseMaxYawLagDeg: number;
  chaseTurnHeightBoost: number;
  chaseTurnHeightFullLateral: number;
  apexAimEnabled: boolean;
  apexLookAhead: number;
  apexLookAheadSpeedGain: number;
  apexMaxLateralOffset: number;
  apexOffTrackFadeStart: number;
  apexOffTrackFadeEnd: number;
  lookSteerBias: number;
  cameraLateralLean: number;
  bankMaxRollDeg: number;
  bankHalflife: number;
  highSpeedShakeAmp: number;
  accelLagMaxDistance: number;
  accelLagResponsiveness: number;
  accelLagReturnResponsiveness: number;
  /** Seconds to interpolate the camera between modes after `setMode`. */
  modeTransitionDuration: number;

  private readonly anchor = new THREE.Vector3();
  private readonly lookAtPoint = new THREE.Vector3();
  private readonly chasePosition = new THREE.Vector3();
  private readonly chaseLookAt = new THREE.Vector3();
  private readonly chaseLookAtVel = new THREE.Vector3();
  private readonly desiredChasePosition = new THREE.Vector3();
  private readonly smoothedChasePosVel = new THREE.Vector3();
  private readonly shake = new THREE.Vector3();
  private readonly velocity = new THREE.Vector3();
  private readonly prevVelocity = new THREE.Vector3();
  private readonly accelLag = new THREE.Vector3();
  private readonly desiredAccelLag = new THREE.Vector3();
  private readonly topLead = new THREE.Vector3();
  // Smoothed planar follow position for top-down. Lerping this toward the
  // racer's actual position is what produces the "racer pulls ahead in frame"
  // start feel without ever moving the camera backwards in world space.
  private readonly topPosition = new THREE.Vector3();
  // Reusable temporaries so chase/transition math doesn't allocate per frame.
  private readonly tmpLook = new THREE.Vector3();
  private readonly tmpUp = new THREE.Vector3();
  private readonly tmpForward = new THREE.Vector3();
  private readonly tmpRight = new THREE.Vector3();
  private readonly tmpBack = new THREE.Vector3();
  private readonly tmpMatrix = new THREE.Matrix4();
  // Mid-mode-transition state. When non-null, `update()` blends the camera
  // from these captured values toward the new mode's target pose.
  private transitionFromPosition: THREE.Vector3 | null = null;
  private transitionFromLookAt = new THREE.Vector3();
  private transitionFromYaw = 0;
  private transitionFromHorizontal = 0;
  private transitionFromVertical = 0;
  private transitionElapsed = 0;
  private shakeDecay = 0;
  /** Additive FOV bump in degrees, decays after each impact. */
  private fovPunchAmount = 0;
  /** Responsiveness multiplier (1 = nominal, >1 = tighter). Eases back. */
  private responsivenessBoost = 1;
  /** Targeted offset from racer position, world-space. Decays. */
  private readonly dodgeOffset = new THREE.Vector3();
  /** Camera roll target offset (radians) — leans toward wall on contact. */
  private wallRollTarget = 0;
  private wallRollSmoothed = 0;
  private heading = 0;
  private yawRate = 0;
  private smoothedHeading = 0;
  private smoothedHeadingVel = 0;
  private smoothedFov = 0;
  private smoothedFovVel = 0;
  private smoothedBankRoll = 0;
  private smoothedBankRollVel = 0;
  private lastMode: CameraMode;
  private elapsed = 0;
  private cinematicAngle = Math.PI * 0.25;
  private track: Track | null = null;
  /**
   * Height sampler used by chase mode to keep the camera at a constant
   * altitude above terrain (matching the MAP view). When unset, the chase
   * camera anchors against the racer's y instead.
   */
  sampleGroundHeight: GroundHeightSampler | null = null;

  constructor(aspect: number, opts: FollowCameraOptions = {}) {
    this.mode = opts.mode ?? 'topDown';
    this.height = opts.height ?? 30;
    this.tiltDeg = opts.tiltDeg ?? 90;
    this.topLookAhead = opts.topLookAhead ?? 7;
    this.topLeadResponsiveness = opts.topLeadResponsiveness ?? 4;
    this.topLeadFullSpeed = opts.topLeadFullSpeed ?? 45;
    this.topFollowResponsiveness = opts.topFollowResponsiveness ?? 8;
    this.topFovDeg = opts.topFovDeg ?? 50;
    this.chaseDistance = opts.chaseDistance ?? 7.5;
    this.chaseHeight = opts.chaseHeight ?? 2.45;
    this.chaseLookAhead = opts.chaseLookAhead ?? 18;
    this.chasePositionResponsiveness = opts.chasePositionResponsiveness ?? 0.10;
    this.chaseLookResponsiveness = opts.chaseLookResponsiveness ?? 0.22;
    this.chaseTargetHeight = opts.chaseTargetHeight ?? 0.8;
    this.chaseVelocityCompensation = opts.chaseVelocityCompensation ?? 0.015;
    this.chaseFovDeg = opts.chaseFovDeg ?? 47;
    this.chaseFovBoostDeg = opts.chaseFovBoostDeg ?? 58;
    // Speed (m/s) at which the FOV punch fully blooms. Defaults to maxSpeed.
    this.chaseFovFullSpeed = opts.chaseFovFullSpeed ?? 75;
    this.chaseFovResponsiveness = opts.chaseFovResponsiveness ?? 0.5;
    this.chaseHeadingVelocityBlend = opts.chaseHeadingVelocityBlend ?? 0.75;
    this.chaseHeadingDeadzoneYawRate = opts.chaseHeadingDeadzoneYawRate ?? 0.1;
    this.chaseHeadingDeadzoneLateralSpeed = opts.chaseHeadingDeadzoneLateralSpeed ?? 1.5;
    this.chaseMaxYawLagDeg = opts.chaseMaxYawLagDeg ?? 15;
    this.chaseTurnHeightBoost = opts.chaseTurnHeightBoost ?? 1.5;
    this.chaseTurnHeightFullLateral = opts.chaseTurnHeightFullLateral ?? 14;
    this.apexAimEnabled = opts.apexAimEnabled ?? false;
    this.apexLookAhead = opts.apexLookAhead ?? 25;
    this.apexLookAheadSpeedGain = opts.apexLookAheadSpeedGain ?? 18;
    this.apexMaxLateralOffset = opts.apexMaxLateralOffset ?? 4;
    this.apexOffTrackFadeStart = opts.apexOffTrackFadeStart ?? 0.65;
    this.apexOffTrackFadeEnd = opts.apexOffTrackFadeEnd ?? 1.15;
    this.lookSteerBias = opts.lookSteerBias ?? 0;
    this.cameraLateralLean = opts.cameraLateralLean ?? 0.18;
    this.bankMaxRollDeg = opts.bankMaxRollDeg ?? 7;
    this.bankHalflife = opts.bankHalflife ?? 0.25;
    this.highSpeedShakeAmp = opts.highSpeedShakeAmp ?? 0.18;
    // Default 0: previously this offset the camera *backwards* along the
    // motion vector during acceleration, which read as the camera reversing
    // in world space at start. Tuning panel can dial it up for a cinematic
    // pull-back feel; the natural follow-lag from `topFollowResponsiveness`
    // is what produces the start feel out of the box.
    this.accelLagMaxDistance = opts.accelLagMaxDistance ?? 0;
    this.accelLagResponsiveness = opts.accelLagResponsiveness ?? 8;
    this.accelLagReturnResponsiveness = opts.accelLagReturnResponsiveness ?? 3.2;
    this.modeTransitionDuration = opts.modeTransitionDuration ?? 1.0;
    this.lastMode = this.mode;
    this.camera = new THREE.PerspectiveCamera(this.chaseFovDeg, aspect, 0.1, 1500);
    this.smoothedFov = this.camera.fov;
  }

  /**
   * Switch camera mode with a smooth interpolation. The actual transition
   * begins on the next `update()` call so the captured "from" pose is the
   * frame the player just saw.
   */
  setMode(newMode: CameraMode): void {
    if (newMode === this.mode) return;
    this.mode = newMode;
  }

  /**
   * Slow orbit around the racer for the title backdrop. Driven instead of
   * `update()` while `mode === 'title'` so the player sees the live race
   * scene at a cinematic angle while shaders/textures warm up. Position is
   * recomputed each frame from a continuously-incrementing angle, so when
   * we switch back to chase the normal position responsiveness eases the
   * camera into the chase pose — no explicit transition needed.
   */
  updateCinematic(racerPos: THREE.Vector3, dt: number): void {
    this.cinematicAngle = (this.cinematicAngle + dt * CINEMATIC_ORBIT_RADS_PER_SECOND) % (Math.PI * 2);
    const radius = CINEMATIC_ORBIT_RADIUS;
    const height = racerPos.y + CINEMATIC_ORBIT_HEIGHT;
    this.camera.position.set(
      racerPos.x + Math.cos(this.cinematicAngle) * radius,
      height,
      racerPos.z + Math.sin(this.cinematicAngle) * radius,
    );
    this.camera.lookAt(racerPos.x, racerPos.y + CINEMATIC_LOOK_AT_HEIGHT, racerPos.z);
    // Restore the user-configured chase FOV so the cinematic isn't stuck at
    // whatever the last race frame baked in.
    if (this.camera.fov !== this.chaseFovDeg) {
      this.camera.fov = this.chaseFovDeg;
      this.camera.updateProjectionMatrix();
    }
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  setTrack(track: Track | null): void {
    this.track = track;
  }

  resetTo(racerPos: THREE.Vector3, racerHeading: number): void {
    this.anchor.copy(racerPos);
    this.lookAtPoint.copy(racerPos);
    this.chasePosition.copy(racerPos);
    this.chaseLookAt.copy(racerPos);
    this.chaseLookAtVel.set(0, 0, 0);
    this.smoothedChasePosVel.set(0, 0, 0);
    this.topPosition.copy(racerPos);
    this.velocity.set(0, 0, 0);
    this.prevVelocity.set(0, 0, 0);
    this.accelLag.set(0, 0, 0);
    this.desiredAccelLag.set(0, 0, 0);
    this.topLead.set(0, 0, 0);
    this.heading = racerHeading;
    this.yawRate = 0;
    this.smoothedHeading = racerHeading;
    this.smoothedHeadingVel = 0;
    this.smoothedFov = this.mode === 'chase' ? this.chaseFovDeg : this.topFovDeg;
    this.smoothedFovVel = 0;
    this.smoothedBankRoll = 0;
    this.smoothedBankRollVel = 0;
    this.transitionFromPosition = null;
    this.transitionElapsed = 0;
    this.elapsed = 0;
    this.snapTransform();
  }

  shakeImpulse(strength: number): void {
    this.shake.set(
      (Math.random() * 2 - 1) * strength,
      0,
      (Math.random() * 2 - 1) * strength,
    );
    this.shakeDecay = 1;
  }

  /**
   * Punch the chase FOV outward by `degrees` so the world appears to lurch
   * away on impact, decaying back over ~200 ms. Multiple punches take the
   * larger value rather than stacking — chained hits should feel like one
   * sustained whoosh, not a runaway zoom.
   */
  fovPunch(degrees: number): void {
    if (!Number.isFinite(degrees) || degrees <= 0) return;
    if (degrees > this.fovPunchAmount) this.fovPunchAmount = degrees;
  }

  /**
   * Tighten the chase position spring so the camera catches up faster
   * after a sudden velocity change. Used on impact to prevent the slow
   * baseline responsiveness from looking like camera lag. `boost` is a
   * multiplier on chasePositionResponsiveness (e.g. 4 = 4× tighter).
   * Decays back to 1.0 over ~600 ms.
   */
  tightenFollow(boost: number): void {
    if (!Number.isFinite(boost) || boost <= 1) return;
    if (boost > this.responsivenessBoost) this.responsivenessBoost = boost;
  }

  /**
   * Continuous lateral lean toward the wall the racer is contacting.
   * `radians` is signed: positive = right side of camera, negative = left.
   * Smoothed each frame; pass 0 when no contact to release. Settles to a
   * critically-damped resting pose at the target.
   */
  setWallContactRoll(radians: number): void {
    this.wallRollTarget = Number.isFinite(radians) ? radians : 0;
  }

  /**
   * One-shot positional dodge in world space — used to "flinch" the
   * camera away from the wall on impact. Decays back to zero over
   * ~350 ms; new calls take the larger magnitude per axis.
   */
  cameraDodge(deltaX: number, deltaZ: number): void {
    if (!Number.isFinite(deltaX) || !Number.isFinite(deltaZ)) return;
    if (Math.abs(deltaX) > Math.abs(this.dodgeOffset.x)) this.dodgeOffset.x = deltaX;
    if (Math.abs(deltaZ) > Math.abs(this.dodgeOffset.z)) this.dodgeOffset.z = deltaZ;
  }

  update(
    racerPos: THREE.Vector3,
    racerVelocity: THREE.Vector3,
    racerHeading: number,
    dt: number,
    racerYawRate = 0,
  ): void {
    this.anchor.copy(racerPos);
    this.updateAccelerationLag(racerVelocity, dt);
    this.velocity.copy(racerVelocity);
    this.heading = racerHeading;
    this.yawRate = racerYawRate;
    this.elapsed += dt;
    const previousMode = this.lastMode;
    const modeChanged = this.mode !== previousMode;
    this.lastMode = this.mode;

    if (this.shakeDecay > 0) {
      this.shakeDecay = Math.max(0, this.shakeDecay - dt * 4);
      this.shake.multiplyScalar(0.92);
    }
    if (this.fovPunchAmount > 0) {
      // ~200 ms decay envelope; 5/sec exponential plus a small floor so the
      // last fraction of a degree disappears rather than lingering.
      this.fovPunchAmount = Math.max(0, this.fovPunchAmount * Math.exp(-dt * 5) - dt * 0.5);
    }
    if (this.responsivenessBoost > 1) {
      // ~600 ms ease-back envelope (rate 3/sec → ~37% per 300ms).
      this.responsivenessBoost = Math.max(1, 1 + (this.responsivenessBoost - 1) * Math.exp(-dt * 3));
      if (this.responsivenessBoost - 1 < 0.01) this.responsivenessBoost = 1;
    }
    // Camera dodge offset decays exponentially (~350 ms time constant).
    if (this.dodgeOffset.lengthSq() > 1e-6) {
      this.dodgeOffset.multiplyScalar(Math.exp(-dt * 6));
      if (this.dodgeOffset.lengthSq() < 1e-6) this.dodgeOffset.set(0, 0, 0);
    }
    // Smooth the wall-contact roll target. Critically-damped pull toward
    // the target so the lean follows contact intensity smoothly.
    const rollRate = 1 - Math.exp(-dt * 6);
    this.wallRollSmoothed += (this.wallRollTarget - this.wallRollSmoothed) * rollRate;
    if (this.mode === 'topDown') {
      this.updateTopLead(dt);
      this.updateTopFollow(dt);
    }

    if (modeChanged) this.beginTransition(previousMode);

    if (this.transitionFromPosition) {
      this.applyTransition(dt);
    } else {
      this.applyTransform(dt);
    }
  }

  private applyTransform(dt: number): void {
    if (this.mode === 'chase') {
      this.applyChaseTransform(dt);
      return;
    }
    this.applyTopDownTransform();
  }

  private beginTransition(previousMode: CameraMode): void {
    // Capture the camera's current pose so we can interpolate from it. This
    // happens AFTER the previous frame's apply*Transform, so position/up are
    // the values the player just saw, and lookAtPoint reflects what we
    // looked at last frame.
    if (this.transitionFromPosition === null) {
      this.transitionFromPosition = new THREE.Vector3();
    }
    this.transitionFromPosition.copy(this.camera.position);
    this.transitionFromLookAt.copy(this.lookAtPoint);
    this.captureTransitionRig(previousMode);
    this.transitionElapsed = 0;
  }

  private captureTransitionRig(previousMode: CameraMode): void {
    if (!this.transitionFromPosition) return;

    const offsetX = this.transitionFromPosition.x - this.transitionFromLookAt.x;
    const offsetY = this.transitionFromPosition.y - this.transitionFromLookAt.y;
    const offsetZ = this.transitionFromPosition.z - this.transitionFromLookAt.z;
    this.transitionFromHorizontal = Math.hypot(offsetX, offsetZ);
    this.transitionFromVertical = offsetY;
    this.transitionFromYaw = this.transitionFromHorizontal > 1e-3
      ? Math.atan2(-offsetX, -offsetZ)
      : previousMode === 'chase'
        ? this.heading
        : 0;
  }

  private applyTransition(dt: number): void {
    if (!this.transitionFromPosition) return;

    this.transitionElapsed += dt;
    const dur = Math.max(1e-3, this.modeTransitionDuration);
    const raw = THREE.MathUtils.clamp(this.transitionElapsed / dur, 0, 1);
    const t = raw * raw * (3 - 2 * raw); // smoothstep ease
    const target = this.computeModeRigTarget();
    const yaw = this.lerpHeading(this.transitionFromYaw, target.yaw, t);
    const horizontal = THREE.MathUtils.lerp(
      this.transitionFromHorizontal,
      target.horizontal,
      t,
    );
    const vertical = THREE.MathUtils.lerp(this.transitionFromVertical, target.vertical, t);

    this.lookAtPoint.lerpVectors(this.transitionFromLookAt, target.focus, t);
    this.applyOrbitTransform(this.lookAtPoint, yaw, horizontal, vertical);

    if (raw >= 1) {
      // Seed the new mode's smoothed state so the first non-transition frame
      // doesn't jump back from the just-reached pose.
      if (this.mode === 'chase') {
        this.chasePosition.copy(this.camera.position);
        this.chaseLookAt.copy(this.lookAtPoint);
        this.chaseLookAtVel.set(0, 0, 0);
        this.smoothedChasePosVel.set(0, 0, 0);
        this.smoothedHeading = this.heading;
        this.smoothedHeadingVel = 0;
      } else {
        // Reverse the topdown lookAt formula:
        //   lookAt = topPosition + topLead + accelLag
        this.topPosition.set(
          this.lookAtPoint.x - this.topLead.x - this.accelLag.x,
          this.anchor.y,
          this.lookAtPoint.z - this.topLead.z - this.accelLag.z,
        );
      }
      this.transitionFromPosition = null;
    }
  }

  private lerpHeading(from: number, to: number, t: number): number {
    const delta = Math.atan2(Math.sin(to - from), Math.cos(to - from));
    return from + delta * t;
  }

  private computeModeRigTarget(): {
    focus: THREE.Vector3;
    yaw: number;
    horizontal: number;
    vertical: number;
  } {
    if (this.mode === 'chase') {
      const forwardX = Math.sin(this.heading);
      const forwardZ = Math.cos(this.heading);
      const velocityCompensation = Math.max(0, this.chaseVelocityCompensation);
      const aX = this.anchor.x + this.velocity.x * velocityCompensation + this.accelLag.x;
      const aZ = this.anchor.z + this.velocity.z * velocityCompensation + this.accelLag.z;
      this.tmpLook.set(
        aX + forwardX * this.chaseLookAhead,
        this.anchor.y + this.chaseTargetHeight,
        aZ + forwardZ * this.chaseLookAhead,
      );
      return {
        focus: this.tmpLook,
        yaw: this.heading,
        horizontal: this.chaseDistance + this.chaseLookAhead,
        vertical: this.chaseHeight - this.chaseTargetHeight,
      };
    } else {
      const tilt = THREE.MathUtils.degToRad(this.tiltDeg);
      const horizontal = Math.cos(tilt) * this.height;
      const vertical = Math.sin(tilt) * this.height;
      this.tmpLook.set(
        this.anchor.x + this.topLead.x + this.accelLag.x,
        this.anchor.y,
        this.anchor.z + this.topLead.z + this.accelLag.z,
      );
      return {
        focus: this.tmpLook,
        yaw: 0,
        horizontal,
        vertical,
      };
    }
  }

  private applyOrbitTransform(
    focus: THREE.Vector3,
    yaw: number,
    horizontal: number,
    vertical: number,
  ): void {
    const forwardX = Math.sin(yaw);
    const forwardZ = Math.cos(yaw);
    this.camera.position.set(
      focus.x - forwardX * horizontal,
      focus.y + vertical,
      focus.z - forwardZ * horizontal,
    );

    this.tmpForward.set(
      focus.x - this.camera.position.x,
      focus.y - this.camera.position.y,
      focus.z - this.camera.position.z,
    ).normalize();
    this.tmpRight.set(-Math.cos(yaw), 0, Math.sin(yaw)).normalize();
    this.tmpBack.copy(this.tmpForward).multiplyScalar(-1);
    this.tmpUp.crossVectors(this.tmpBack, this.tmpRight).normalize();
    this.tmpMatrix.makeBasis(this.tmpRight, this.tmpUp, this.tmpBack);
    this.camera.quaternion.setFromRotationMatrix(this.tmpMatrix);
    this.camera.up.copy(this.tmpUp);
  }

  private snapTransform(): void {
    if (this.mode === 'chase') {
      this.applyChaseTransform(1, true);
      return;
    }
    this.topPosition.copy(this.anchor);
    this.applyTopDownTransform();
  }

  private updateTopFollow(dt: number): void {
    const alpha = 1 - Math.exp(-Math.max(0, this.topFollowResponsiveness) * dt);
    this.topPosition.lerp(this.anchor, alpha);
  }

  private applyTopDownTransform(): void {
    this.camera.fov = this.topFovDeg;
    this.camera.updateProjectionMatrix();

    const tilt = THREE.MathUtils.degToRad(this.tiltDeg);
    const horizontal = Math.cos(tilt) * this.height;
    const vertical = Math.sin(tilt) * this.height;

    this.lookAtPoint.set(
      this.topPosition.x + this.topLead.x + this.accelLag.x,
      this.anchor.y,
      this.topPosition.z + this.topLead.z + this.accelLag.z,
    );
    this.camera.position.set(
      this.lookAtPoint.x + this.shake.x * this.shakeDecay,
      this.lookAtPoint.y + vertical,
      this.lookAtPoint.z - horizontal + this.shake.z * this.shakeDecay,
    );
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(this.lookAtPoint.x, this.lookAtPoint.y, this.lookAtPoint.z);
  }

  private updateTopLead(dt: number): void {
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    const leadScale = THREE.MathUtils.smoothstep(
      speed / Math.max(1, this.topLeadFullSpeed),
      0.15,
      1,
    );
    const leadDistance = this.topLookAhead * leadScale;
    const desiredX = speed > 0.5
      ? (this.velocity.x / speed) * leadDistance
      : 0;
    const desiredZ = speed > 0.5
      ? (this.velocity.z / speed) * leadDistance
      : 0;
    const alpha = 1 - Math.exp(-this.topLeadResponsiveness * dt);
    this.topLead.x += (desiredX - this.topLead.x) * alpha;
    this.topLead.z += (desiredZ - this.topLead.z) * alpha;
  }

  private applyChaseTransform(dt: number, snap = false): void {
    const sample = this.sampleGroundHeight ?? ((_x: number, _z: number) => this.anchor.y);
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    const speedNorm = THREE.MathUtils.clamp(speed / Math.max(1, this.chaseFovFullSpeed), 0, 1);
    const fovT = THREE.MathUtils.smoothstep(speedNorm, 0, 1);
    const targetFov = THREE.MathUtils.lerp(this.chaseFovDeg, this.chaseFovBoostDeg, fovT);
    if (snap) {
      this.smoothedFov = targetFov;
      this.smoothedFovVel = 0;
    } else {
      const steppedFov = springStepScalar(
        this.smoothedFov,
        this.smoothedFovVel,
        targetFov,
        this.chaseFovResponsiveness,
        dt,
      );
      this.smoothedFov = steppedFov.x;
      this.smoothedFovVel = steppedFov.v;
    }
    this.camera.fov = this.smoothedFov + this.fovPunchAmount;
    this.camera.updateProjectionMatrix();

    if (snap) {
      this.smoothedHeading = this.heading;
      this.smoothedHeadingVel = 0;
    } else {
      const speedHeadingWeight = speed > 2
        ? THREE.MathUtils.smoothstep(speed / Math.max(1, this.chaseFovFullSpeed), 0.05, 0.35)
        : 0;
      const velocityHeading = speed > 2
        ? Math.atan2(this.velocity.x, this.velocity.z)
        : this.heading;
      const headingForwardX = Math.sin(this.heading);
      const headingForwardZ = Math.cos(this.heading);
      const velocityForwardX = speed > 2 ? this.velocity.x / speed : headingForwardX;
      const velocityForwardZ = speed > 2 ? this.velocity.z / speed : headingForwardZ;
      const headingVelocityDot = headingForwardX * velocityForwardX + headingForwardZ * velocityForwardZ;
      const headingRightX = Math.cos(this.heading);
      const headingRightZ = -Math.sin(this.heading);
      const lateralSpeed = Math.abs(this.velocity.x * headingRightX + this.velocity.z * headingRightZ);
      const yawActivity = THREE.MathUtils.smoothstep(
        Math.abs(this.yawRate),
        Math.max(0, this.chaseHeadingDeadzoneYawRate),
        Math.max(1e-3, this.chaseHeadingDeadzoneYawRate * 2),
      );
      const lateralActivity = THREE.MathUtils.smoothstep(
        lateralSpeed,
        Math.max(0, this.chaseHeadingDeadzoneLateralSpeed),
        Math.max(1e-3, this.chaseHeadingDeadzoneLateralSpeed * 2),
      );
      const headingActivity = Math.max(yawActivity, lateralActivity);
      const reverseTravelWeight = THREE.MathUtils.smoothstep(
        (1 - headingVelocityDot) * 0.5,
        0.55,
        0.95,
      );
      const velocityBlend = Math.max(
        this.chaseHeadingVelocityBlend * headingActivity,
        reverseTravelWeight,
      );
      const headingGoal = this.lerpHeading(
        this.heading,
        velocityHeading,
        THREE.MathUtils.clamp(velocityBlend * speedHeadingWeight, 0, 1),
      );
      const steppedHeading = springStepAngle(
        this.smoothedHeading,
        this.smoothedHeadingVel,
        headingGoal,
        this.chaseLookResponsiveness,
        dt,
      );
      this.smoothedHeading = steppedHeading.x;
      this.smoothedHeadingVel = steppedHeading.v;
      const maxYawLag = THREE.MathUtils.degToRad(Math.max(0, this.chaseMaxYawLagDeg));
      const yawLag = Math.atan2(
        Math.sin(this.smoothedHeading - headingGoal),
        Math.cos(this.smoothedHeading - headingGoal),
      );
      if (Math.abs(yawLag) > maxYawLag) {
        this.smoothedHeading = headingGoal + Math.sign(yawLag) * maxYawLag;
        this.smoothedHeadingVel = 0;
      }
    }

    const forwardX = Math.sin(this.smoothedHeading);
    const forwardZ = Math.cos(this.smoothedHeading);
    const rightX = Math.cos(this.smoothedHeading);
    const rightZ = -Math.sin(this.smoothedHeading);
    const velocityCompensation = Math.max(0, this.chaseVelocityCompensation);
    const anchorX = this.anchor.x + this.velocity.x * velocityCompensation + this.accelLag.x;
    const anchorZ = this.anchor.z + this.velocity.z * velocityCompensation + this.accelLag.z;
    const yawRateNorm = THREE.MathUtils.clamp(this.yawRate / 2.3, -1, 1);

    const shakeX = snap ? 0 : this.shake.x * this.shakeDecay;
    const shakeZ = snap ? 0 : this.shake.z * this.shakeDecay;
    const wobbleAmp = snap
      ? 0
      : this.highSpeedShakeAmp * THREE.MathUtils.smoothstep(speedNorm, 0.4, 1.0);
    const wobbleX = Math.sin(this.elapsed * 41.0) * wobbleAmp;
    const wobbleY = Math.cos(this.elapsed * 53.0) * wobbleAmp * 0.5;
    const desiredX = anchorX - forwardX * this.chaseDistance
      - rightX * this.cameraLateralLean * yawRateNorm
      + shakeX
      + wobbleX;
    const desiredZ = anchorZ - forwardZ * this.chaseDistance
      - rightZ * this.cameraLateralLean * yawRateNorm
      + shakeZ;
    this.desiredChasePosition.set(
      desiredX,
      sample(desiredX, desiredZ) + this.chaseHeight + wobbleY,
      desiredZ,
    );

    this.lookAtPoint.set(
      anchorX + forwardX * this.chaseLookAhead,
      sample(this.anchor.x, this.anchor.z) + this.chaseTargetHeight,
      anchorZ + forwardZ * this.chaseLookAhead,
    );
    let turnReadability = 0;
    if (this.track && this.apexAimEnabled) {
      const pos = locateTrackPosition(this.track, this.anchor.x, this.anchor.z);
      const apex = sampleCenterlineAhead(
        this.track,
        pos.arcLength,
        this.apexLookAhead + this.apexLookAheadSpeedGain * speedNorm,
      );
      const corridorHalfWidth = Math.max(1, Math.min(pos.widthLeft, pos.widthRight));
      const lateral01 = Math.abs(pos.signedLateral) / corridorHalfWidth;
      const apexWeight = 1 - THREE.MathUtils.smoothstep(
        lateral01,
        this.apexOffTrackFadeStart,
        Math.max(this.apexOffTrackFadeStart + 1e-3, this.apexOffTrackFadeEnd),
      );
      const baseX = this.lookAtPoint.x;
      const baseY = this.lookAtPoint.y;
      const baseZ = this.lookAtPoint.z;
      const apexDx = apex.x - baseX;
      const apexDz = apex.z - baseZ;
      const apexForward = apexDx * forwardX + apexDz * forwardZ;
      const rawApexLateral = apexDx * rightX + apexDz * rightZ;
      const apexLateral = THREE.MathUtils.clamp(
        rawApexLateral,
        -this.apexMaxLateralOffset,
        this.apexMaxLateralOffset,
      );
      turnReadability = apexWeight * THREE.MathUtils.smoothstep(
        Math.abs(rawApexLateral),
        this.apexMaxLateralOffset,
        Math.max(this.apexMaxLateralOffset + 1e-3, this.chaseTurnHeightFullLateral),
      );
      this.tmpLook.set(
        baseX + forwardX * apexForward + rightX * apexLateral,
        sample(apex.x, apex.z) + this.chaseTargetHeight,
        baseZ + forwardZ * apexForward + rightZ * apexLateral,
      );
      this.tmpLook.y = THREE.MathUtils.lerp(baseY, this.tmpLook.y, apexWeight);
      this.lookAtPoint.lerp(this.tmpLook, apexWeight);
    }
    this.desiredChasePosition.y += this.chaseTurnHeightBoost * turnReadability;
    this.lookAtPoint.x += rightX * this.lookSteerBias * yawRateNorm;
    this.lookAtPoint.z += rightZ * this.lookSteerBias * yawRateNorm;

    if (snap) {
      this.chasePosition.copy(this.desiredChasePosition);
      this.smoothedChasePosVel.set(0, 0, 0);
      this.chaseLookAt.copy(this.lookAtPoint);
      this.chaseLookAtVel.set(0, 0, 0);
      this.smoothedBankRoll = 0;
      this.smoothedBankRollVel = 0;
    } else {
      // Boosted responsiveness on impact lets the position spring catch up
      // to a sudden velocity change in ~150 ms instead of ~600+ ms; eases
      // back to the cruising responsiveness over ~600 ms.
      const positionResp = this.chasePositionResponsiveness * this.responsivenessBoost;
      springStepVec3(
        this.chasePosition,
        this.smoothedChasePosVel,
        this.desiredChasePosition,
        positionResp,
        dt,
      );
      springStepVec3(
        this.chaseLookAt,
        this.chaseLookAtVel,
        this.lookAtPoint,
        this.chaseLookResponsiveness,
        dt,
      );
      const targetBank = THREE.MathUtils.degToRad(this.bankMaxRollDeg) * yawRateNorm;
      const steppedBank = springStepScalar(
        this.smoothedBankRoll,
        this.smoothedBankRollVel,
        targetBank,
        this.bankHalflife,
        dt,
      );
      this.smoothedBankRoll = steppedBank.x;
      this.smoothedBankRollVel = steppedBank.v;
    }

    this.camera.position.copy(this.chasePosition);
    // Camera dodge: one-shot positional flinch away from the wall normal,
    // applied on top of the smoothed chase position. Decays exponentially
    // so the world appears to shove the camera rather than the camera
    // simply lagging.
    if (this.dodgeOffset.lengthSq() > 1e-6) {
      this.camera.position.x += this.dodgeOffset.x;
      this.camera.position.z += this.dodgeOffset.z;
    }
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(this.chaseLookAt.x, this.chaseLookAt.y, this.chaseLookAt.z);
    if (Math.abs(this.smoothedBankRoll) > 1e-5) {
      this.camera.rotateZ(this.smoothedBankRoll);
    }
    // Wall-contact roll: lean the camera toward the wall the racer is
    // grinding so sustained contact reads as physical pressure, not just
    // shake. Applied after lookAt so the lean composes cleanly with bank.
    if (Math.abs(this.wallRollSmoothed) > 1e-5) {
      this.camera.rotateZ(this.wallRollSmoothed);
    }
  }

  private updateAccelerationLag(racerVelocity: THREE.Vector3, dt: number): void {
    const safeDt = Math.max(dt, 1 / 240);
    const speed = Math.hypot(racerVelocity.x, racerVelocity.z);
    this.desiredAccelLag.set(0, 0, 0);
    if (speed > 0.5) {
      const accelX = (racerVelocity.x - this.prevVelocity.x) / safeDt;
      const accelZ = (racerVelocity.z - this.prevVelocity.z) / safeDt;
      const dirX = racerVelocity.x / speed;
      const dirZ = racerVelocity.z / speed;
      const forwardAccel = Math.max(0, accelX * dirX + accelZ * dirZ);
      const accelSignal = THREE.MathUtils.smoothstep(forwardAccel, 4, 38);
      const distance = this.accelLagMaxDistance * accelSignal;
      this.desiredAccelLag.set(-dirX * distance, 0, -dirZ * distance);
    }

    const returning = this.desiredAccelLag.lengthSq() < this.accelLag.lengthSq();
    const response = returning ? this.accelLagReturnResponsiveness : this.accelLagResponsiveness;
    const alpha = 1 - Math.exp(-Math.max(0, response) * safeDt);
    this.accelLag.lerp(this.desiredAccelLag, alpha);
    this.prevVelocity.copy(racerVelocity);
  }
}
